import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BlogCategories from './BlogCategories';
import { HiOutlineArrowLongRight } from 'react-icons/hi2';
import { fetchPosts } from '../../../lib/fetchBlogs';
import { getPreviewText } from '../../../util/util';

export default function Blogs() {
  const [searchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function getBlogs() {
      setIsLoading(true);
      try {
        const posts = await fetchPosts();
        setBlogs(posts);
        setFilteredBlogs(posts);
      } catch (error) {
        console.error('Failed to fetch blogs:', error);
      } finally {
        setIsLoading(false);
      }
    }
    getBlogs();
  }, []);

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredBlogs(blogs);
    } else {
      const filtered = blogs.filter((blog) =>
        blog.categories?.some((category) => category.title === selectedCategory)
      );
      setFilteredBlogs(filtered);
    }
  }, [selectedCategory, blogs]);

  const onCategoryHandler = (category) => {
    setSelectedCategory(category);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="container blog-list-page">
      <section className="section">
        <aside className="aside">
          <div className="category-list">
            <BlogCategories
              selectedCategory={selectedCategory}
              onCategoryHandler={onCategoryHandler}
            />
          </div>
        </aside>

        <main className="main">
          <div className="article-grid">
            {isLoading ? (
              <div className="loading-blog-text">Loading...</div>
            ) : filteredBlogs.length > 0 ? (
              filteredBlogs.map((blog) => (
                <article key={blog._id || blog.slug?.current} className="article">
                  {blog.mainImage?.asset?.url ? (
                    <img
                      src={blog.mainImage.asset.url}
                      alt={blog.mainImage.alt || blog.title || 'Blog image'}
                      className="article-img"
                      width={1000}
                      height={600}
                      loading="lazy"
                    />
                  ) : (
                    <div className="article-img article-img-placeholder" aria-hidden="true" />
                  )}
                  <div className="news-content">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      {blog.author?.image?.asset?.url && (
                        <img
                          src={blog.author.image.asset.url}
                          alt={blog.author.name}
                          className="w-8 h-8 rounded-full mr-2 object-cover"
                        />
                      )}
                      <div>
                        {blog.author?.name && (
                          <Link
                            to={`/author/${blog.author.slug?.current || ''}`}
                            className="text-blue-600 hover:underline"
                          >
                            {blog.author.name}
                          </Link>
                        )}
                        {blog.publishedAt && (
                          <span className="text-gray-400 ml-2">• {formatDate(blog.publishedAt)}</span>
                        )}
                      </div>
                    </div>
                    <h4 className="text-xl font-bold mb-2">{blog.title}</h4>
                    {blog.excerpt ? (
                      <p className="text-gray-600 mb-4">{blog.excerpt}</p>
                    ) : (
                      <p className="text-gray-600 mb-4">{getPreviewText(blog.body, 150)}</p>
                    )}
                    <Link
                      to={`/blog/${blog.slug?.current}`}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <span className="mr-1">Read more</span>
                      <HiOutlineArrowLongRight className="w-5 h-5" />
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <p className="loading-blog-text">No blogs found for this category.</p>
            )}
          </div>
        </main>
      </section>
    </div>
  );
}
